import Header from "../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import {
    Alert, Avatar, Box, Breadcrumbs, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, InputAdornment, Link, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { FormatDate } from "../../utils/FormatDate";

const corPrimaria = "#0b243d";

// Status vindos do Asaas (o back grava "PENDENTE" como padrão)
const STATUS_ASSINATURA = {
    ACTIVE: { label: "Ativa", color: "success" },
    PENDENTE: { label: "Pendente", color: "warning" },
    INACTIVE: { label: "Inativa", color: "default" },
    EXPIRED: { label: "Expirada", color: "error" },
};
const statusAssinatura = (status) => STATUS_ASSINATURA[status] || { label: status || "—", color: "default" };

const filtros = [
    { chave: "todos", label: "Todas" },
    { chave: "ACTIVE", label: "Ativas" },
    { chave: "PENDENTE", label: "Pendentes" },
    { chave: "inativas", label: "Inativas e expiradas", status: ["INACTIVE", "EXPIRED"] },
];

const DIA_MS = 24 * 60 * 60 * 1000;
const diasAte = (data) => (data ? Math.ceil((new Date(data).getTime() - Date.now()) / DIA_MS) : null);
const formatarData = (data) => (data ? FormatDate(data) : "—");

const normalizar = (texto) => String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
// O back seleciona o atributo "Nome" (o model usa "nome"); aceita os dois
const nomeCliente = (assinatura) => assinatura?.usuario?.nome || assinatura?.usuario?.Nome || "";
const iniciais = (nome) => String(nome || "?").trim().split(/\s+/).slice(0, 2).map((parte) => parte[0]?.toUpperCase()).join("");
const capitalizar = (texto) => (texto ? texto.charAt(0) + texto.slice(1).toLowerCase() : "—");

// Cartão de indicador do topo
function Indicador({ titulo, valor, detalhe, cor }) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, p: 2, borderLeft: `4px solid ${cor}` }}>
            <Typography variant="body2" color="text.secondary">{titulo}</Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 600, lineHeight: 1.3 }}>{valor}</Typography>
            {detalhe && <Typography variant="caption" color="text.secondary">{detalhe}</Typography>}
        </Card>
    );
}

function Assinaturas() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [assinaturas, setAssinaturas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState("");
    const [loading, setLoading] = useState(false);
    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState("todos");
    const [ordemCobranca, setOrdemCobranca] = useState("asc");
    const [pagina, setPagina] = useState(0);
    const [porPagina, setPorPagina] = useState(10);
    const [assinaturaParaDesativar, setAssinaturaParaDesativar] = useState(null);


    useEffect(() => {
        const getAssinaturas = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarAssinaturas`, {
                    headers: {
                        Authorization: token
                    }
                });
                if (response.data.status !== 200) {
                    setErro(response.data.erro || "Não foi possível carregar as assinaturas.");
                }
                setAssinaturas(response.data.assinaturas || []);
            } catch (err) {
                console.error(err);
                setErro("Não foi possível carregar as assinaturas. Tente novamente mais tarde.");
            } finally {
                setCarregando(false);
            }
        };
        getAssinaturas();
    }, [token]);

    const handleDesativateAssinatura = async (idAssinatura) => {
        alert(`Ocorreu um erro ao tentar desativar a assinatura de ID ${idAssinatura}.`);
        // try {
        //     setLoading(true);
        //     const response = await axios.delete(`${process.env.REACT_APP_API_URL}/desativarAssinatura/${idAssinatura}`, {
        //         headers: {
        //             Authorization: token
        //         }
        //     });
        //     console.log(response);
        // } catch (error) {
        //     console.error(error);
        // } finally {
        //     setLoading(false);
        // }
    }

    const confirmarDesativacao = async () => {
        await handleDesativateAssinatura(assinaturaParaDesativar.idAssinatura);
        setAssinaturaParaDesativar(null);
    };

    const indicadores = useMemo(() => {
        const contar = (status) => assinaturas.filter((a) => status.includes(a.status)).length;
        const proximas7Dias = assinaturas.filter((a) => {
            const dias = diasAte(a.proximaCobranca);
            return a.status === "ACTIVE" && dias !== null && dias >= 0 && dias <= 7;
        }).length;
        return {
            ativas: contar(["ACTIVE"]),
            pendentes: contar(["PENDENTE"]),
            inativas: contar(["INACTIVE", "EXPIRED"]),
            proximas7Dias,
        };
    }, [assinaturas]);

    const contagemFiltro = {
        todos: assinaturas.length,
        ACTIVE: indicadores.ativas,
        PENDENTE: indicadores.pendentes,
        inativas: indicadores.inativas,
    };

    const assinaturasFiltradas = useMemo(() => {
        const termo = normalizar(busca.trim());
        const filtroAtual = filtros.find((f) => f.chave === filtro);
        const statusDoFiltro = filtroAtual?.status || [filtroAtual?.chave];

        return assinaturas
            .filter((a) => filtro === "todos" || statusDoFiltro.includes(a.status))
            .filter((a) => !termo || [nomeCliente(a), a.plano?.nome, String(a.idAssinatura)].some((campo) => normalizar(campo).includes(termo)))
            .sort((a, b) => {
                // Sem próxima cobrança vai sempre para o fim
                const dataA = a.proximaCobranca ? new Date(a.proximaCobranca).getTime() : Infinity;
                const dataB = b.proximaCobranca ? new Date(b.proximaCobranca).getTime() : Infinity;
                if (dataA === dataB) return 0;
                if (dataA === Infinity) return 1;
                if (dataB === Infinity) return -1;
                return ordemCobranca === "asc" ? dataA - dataB : dataB - dataA;
            });
    }, [assinaturas, busca, filtro, ordemCobranca]);

    const assinaturasDaPagina = assinaturasFiltradas.slice(pagina * porPagina, pagina * porPagina + porPagina);

    const corProximaCobranca = (assinatura) => {
        if (assinatura.status !== "ACTIVE") return "text.secondary";
        const dias = diasAte(assinatura.proximaCobranca);
        if (dias === null) return "text.secondary";
        if (dias < 0) return "error.main";
        if (dias <= 7) return "warning.main";
        return "text.primary";
    };


    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Assinaturas</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Assinaturas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Acompanhe os planos contratados pelos clientes, o status no Asaas e as próximas cobranças.
                        </Typography>
                    </Box>

                    {/* Indicadores */}
                    <Box sx={{ display: "grid", gap: 2, mb: 3, gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" } }}>
                        {carregando ? (
                            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={96} />)
                        ) : (
                            <>
                                <Indicador titulo="Ativas" valor={indicadores.ativas} cor="#2e7d32" />
                                <Indicador titulo="Pendentes" valor={indicadores.pendentes} cor="#ed6c02" />
                                <Indicador titulo="Inativas e expiradas" valor={indicadores.inativas} cor="#9e9e9e" />
                                <Indicador titulo="Cobranças em 7 dias" valor={indicadores.proximas7Dias} detalhe="Assinaturas ativas" cor={corPrimaria} />
                            </>
                        )}
                    </Box>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        {/* Busca e filtros */}
                        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar por cliente, plano ou ID da assinatura"
                                value={busca}
                                onChange={(e) => { setBusca(e.target.value); setPagina(0); }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {filtros.map((item) => {
                                    const ativo = filtro === item.chave;
                                    return (
                                        <Chip
                                            key={item.chave}
                                            label={`${item.label} (${contagemFiltro[item.chave] || 0})`}
                                            size="small"
                                            variant={ativo ? "filled" : "outlined"}
                                            onClick={() => { setFiltro(item.chave); setPagina(0); }}
                                            sx={ativo ? { backgroundColor: corPrimaria, color: "#fff", "&:hover": { backgroundColor: "#153d66" } } : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {erro && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{erro}</Alert>}

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 860 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Plano</TableCell>
                                        <TableCell>Pagamento</TableCell>
                                        <TableCell>Início</TableCell>
                                        <TableCell sortDirection={ordemCobranca}>
                                            <TableSortLabel
                                                active
                                                direction={ordemCobranca}
                                                onClick={() => setOrdemCobranca((o) => (o === "asc" ? "desc" : "asc"))}
                                            >
                                                Próxima cobrança
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 7 }).map((__, j) => (
                                                    <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : assinaturasDaPagina.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <CardMembershipIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>Nenhuma assinatura encontrada</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {busca || filtro !== "todos" ? "Tente outro termo de busca ou filtro." : "Ainda não há assinaturas."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        assinaturasDaPagina.map((assinatura) => {
                                            const status = statusAssinatura(assinatura.status);
                                            const nome = nomeCliente(assinatura);
                                            const dias = diasAte(assinatura.proximaCobranca);
                                            return (
                                                <TableRow key={assinatura.idAssinatura} hover sx={{ "& td": { py: 1.5 } }}>
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{ width: 36, height: 36, fontSize: 14, backgroundColor: corPrimaria }}>
                                                                {iniciais(nome)}
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Link
                                                                    component="button"
                                                                    underline="hover"
                                                                    variant="body2"
                                                                    sx={{ fontWeight: 500, color: "text.primary", textAlign: "left" }}
                                                                    onClick={() => navigate(`/EmitirNota/Perfil/${assinatura.idUsuario}`)}
                                                                >
                                                                    {nome || "Cliente sem nome"}
                                                                </Link>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                    Assinatura #{assinatura.idAssinatura}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{assinatura.plano?.nome || "—"}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{capitalizar(assinatura.periodicidade)}</Typography>
                                                    </TableCell>
                                                    <TableCell>{capitalizar(assinatura.metodoPagamento)}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatarData(assinatura.dataInicio)}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                        <Typography variant="body2" sx={{ color: corProximaCobranca(assinatura) }}>
                                                            {formatarData(assinatura.proximaCobranca)}
                                                        </Typography>
                                                        {assinatura.status === "ACTIVE" && dias !== null && dias <= 7 && (
                                                            <Typography variant="caption" sx={{ color: corProximaCobranca(assinatura) }}>
                                                                {dias < 0 ? `Atrasada há ${Math.abs(dias)} dia(s)` : dias === 0 ? "Hoje" : `Em ${dias} dia(s)`}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small" variant="outlined" color={status.color} label={status.label} />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                        <Tooltip title="Editar (em breve)">
                                                            <span>
                                                                <IconButton size="small" disabled aria-label="Editar assinatura">
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Desativar assinatura">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                aria-label="Desativar assinatura"
                                                                onClick={() => setAssinaturaParaDesativar(assinatura)}
                                                            >
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!carregando && assinaturasFiltradas.length > 0 && (
                            <TablePagination
                                component="div"
                                count={assinaturasFiltradas.length}
                                page={pagina}
                                onPageChange={(e, novaPagina) => setPagina(novaPagina)}
                                rowsPerPage={porPagina}
                                onRowsPerPageChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(0); }}
                                rowsPerPageOptions={[10, 25, 50]}
                                labelRowsPerPage="Por página"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                                sx={{ borderTop: 1, borderColor: "divider" }}
                            />
                        )}
                    </Card>
                </Box>
            </Box>

            {/* Confirmação antes de desativar */}
            <Dialog open={!!assinaturaParaDesativar} onClose={() => !loading && setAssinaturaParaDesativar(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Desativar assinatura</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Desativar a assinatura #{assinaturaParaDesativar?.idAssinatura} de <strong>{nomeCliente(assinaturaParaDesativar) || "cliente sem nome"}</strong> ({assinaturaParaDesativar?.plano?.nome || "sem plano"})?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button size="small" sx={{ textTransform: "none" }} disabled={loading} onClick={() => setAssinaturaParaDesativar(null)}>
                        Cancelar
                    </Button>
                    <Button size="small" variant="contained" color="error" sx={{ textTransform: "none" }} loading={loading} onClick={confirmarDesativacao}>
                        Desativar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Assinaturas;
